import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Linkedin, Mail, MessageSquare, MessageCircle, FileText, Loader2, CheckCircle, AlertCircle, Clock, XCircle, RefreshCw, Settings, Video } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSettings } from "@/providers/SettingsProvider";
import { useFeatureFlag } from "@/providers/FeatureFlagsProvider";
import { useTranslation } from "react-i18next";
import { ConnectionProgressBanner } from "@/components/ui/ConnectionProgressBanner";

export const IntegrationsTab = () => {
	const [integrations, setIntegrations] = useState<{
		[key: string]: {
			connected: boolean;
			status?: string;
			accountId?: string;
			providerType?: string;
		}
	}>({
		linkedin: { connected: false, status: undefined, accountId: undefined, providerType: undefined },
		email: { connected: false, status: undefined, accountId: undefined, providerType: undefined },
		whatsapp: { connected: false, status: undefined, accountId: undefined, providerType: undefined },
		microsoft_teams: { connected: false, status: undefined, accountId: undefined, providerType: undefined }
	});
	const [loading, setLoading] = useState(true);
	const [processingProvider, setProcessingProvider] = useState<string | null>(null);
	const [resyncingProvider, setResyncingProvider] = useState<string | null>(null);
	const [disconnectDialog, setDisconnectDialog] = useState<{
		open: boolean;
		provider: string;
		accountId?: string;
	}>({ open: false, provider: "" });
	const [emailProviderDialog, setEmailProviderDialog] = useState(false);
	const [calendarSettingsDialog, setCalendarSettingsDialog] = useState(false);
	const [calendars, setCalendars] = useState<any[]>([]);
	const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null);
	const [loadingCalendars, setLoadingCalendars] = useState(false);
	const [savingCalendar, setSavingCalendar] = useState(false);
	const [searchParams] = useSearchParams();
	const processedStatusRef = useRef<string | null>(null);
	const [hasIncompleteAccounts, setHasIncompleteAccounts] = useState(false);
	const reloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const previousIncompleteState = useRef<boolean>(false);
	const { t } = useTranslation();
	const transcriptsEnabled = useFeatureFlag("transcripts");

	// Fetch existing integrations on component mount and when status param changes
	useEffect(() => {
		fetchIntegrations();
		
		// Handle redirect with status parameter (Unipile)
		const status = searchParams.get('status');
		if (status && status !== processedStatusRef.current) {
			processedStatusRef.current = status;
			handleConnectionResult(status);
		}
		// Handle Microsoft Teams OAuth callback
		const msStatus = searchParams.get('microsoft_status');
		if (msStatus && msStatus !== processedStatusRef.current) {
			processedStatusRef.current = msStatus;
			handleConnectionResult(msStatus);
		}
	}, [searchParams]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (reloadTimeoutRef.current) {
				clearTimeout(reloadTimeoutRef.current);
				reloadTimeoutRef.current = null;
			}
		};
	}, []);

	// Auto-reload when incomplete accounts are detected - simplified
	useEffect(() => {
		console.log("Auto-reload useEffect triggered, hasIncompleteAccounts:", hasIncompleteAccounts);
		// The timeout management is now handled directly in fetchIntegrations
		// This useEffect is mainly for logging state changes
	}, [hasIncompleteAccounts]);

	// Monitor hasIncompleteAccounts state changes
	useEffect(() => {
		console.log("hasIncompleteAccounts state changed to:", hasIncompleteAccounts);
	}, [hasIncompleteAccounts]);

	const fetchIntegrations = async (showLoading = true) => {
		try {
			if (showLoading)
				setLoading(true);
			
			const currentUser = await supabase.auth.getUser();
			if (currentUser.error || !currentUser.data.user) {
				throw new Error("User not authenticated");
			}

			// Query unipile_account table for the current user
			const { data: unipileAccounts, error } = await supabase
				.from('unipile_account')
				.select('*')
				.eq('user_id', currentUser.data.user.id);

			if (error) {
				console.error("Error fetching integrations:", error);
				throw error;
			}

			const newIntegrationsState = {
				linkedin: { connected: false, status: undefined, accountId: undefined, providerType: undefined },
				email: { connected: false, status: undefined, accountId: undefined, providerType: undefined },
				whatsapp: { connected: false, status: undefined, accountId: undefined, providerType: undefined },
				microsoft_teams: { connected: false, status: undefined, accountId: undefined, providerType: undefined }
			};

			let foundIncompleteAccounts = false;

			// Update state based on the query results
			if (unipileAccounts && unipileAccounts.length > 0) {
				console.log("Processing", unipileAccounts.length, "accounts:", unipileAccounts);
				unipileAccounts.forEach((account, index) => {
					console.log(`Account ${index + 1}:`, account.id, "type:", account.type, "status:", account.status);
					// Check if account exists but is incomplete (missing type or status)
					if (!account.type || !account.status) {
						foundIncompleteAccounts = true;
						console.log("Found incomplete account:", account.id, "type:", account.type, "status:", account.status);
						return; // Skip processing this account
					}

					const accountType = account.type?.toLowerCase();
					console.log("Processing complete account:", account.id, "accountType:", accountType);
					if (accountType === 'linkedin') {
						newIntegrationsState.linkedin = {
							connected: true,
							status: account.status,
							accountId: account.id,
							providerType: accountType
						};
					} else if (accountType === 'google' || accountType === 'google_oauth' || accountType === 'outlook') {
						newIntegrationsState.email = {
							connected: true,
							status: account.status,
							accountId: account.id,
							providerType: accountType
						};
					} else if (accountType === 'whatsapp') {
						newIntegrationsState.whatsapp = {
							connected: true,
							status: account.status,
							accountId: account.id,
							providerType: accountType
						};
					}
				});
			} else {
				console.log("No accounts found in database");
			}

			console.log("Final foundIncompleteAccounts value:", foundIncompleteAccounts);

			// Check Microsoft Teams connection (separate from Unipile)
			try {
				const { data: msToken, error: msError } = await supabase
					.from('microsoft_oauth_tokens')
					.select('id, token_expires_at')
					.eq('user_id', currentUser.data.user.id)
					.single();

				if (!msError && msToken) {
					const isExpired = new Date(msToken.token_expires_at) < new Date();
					newIntegrationsState.microsoft_teams = {
						connected: true,
						status: isExpired ? 'CREDENTIALS' : 'OK',
						accountId: msToken.id,
						providerType: 'microsoft_teams'
					};
				}
			} catch {
				// Microsoft Teams token check is non-critical
			}

			setIntegrations(newIntegrationsState);
			
			// Update incomplete accounts state with explicit comparison
			console.log("Current hasIncompleteAccounts:", hasIncompleteAccounts, "Found incomplete:", foundIncompleteAccounts);
			console.log("Previous incomplete state:", previousIncompleteState.current);
			
			// Always update the state, even if it seems the same
			setHasIncompleteAccounts(() => {
				console.log("Setting hasIncompleteAccounts to:", foundIncompleteAccounts);
				previousIncompleteState.current = foundIncompleteAccounts;
				return foundIncompleteAccounts;
			});
			
			// Handle auto-reload logic directly here
			if (foundIncompleteAccounts) {
				console.log("Incomplete accounts detected, setting up retry in 2 seconds");
				// Clear any existing timeout
				if (reloadTimeoutRef.current) {
					clearTimeout(reloadTimeoutRef.current);
					reloadTimeoutRef.current = null;
				}
				// Set up new timeout
				reloadTimeoutRef.current = setTimeout(() => {
					console.log("Auto-reloading integrations...");
					fetchIntegrations(false); // Don't show loading spinner on auto-reload
				}, 2000);
			} else {
				console.log("All accounts are complete - clearing any existing timeouts");
				// Clear timeout if no incomplete accounts
				if (reloadTimeoutRef.current) {
					clearTimeout(reloadTimeoutRef.current);
					reloadTimeoutRef.current = null;
				}
			}
		} catch (error) {
			console.error("Failed to fetch integrations:", error);
			toast({
				title: t("common.error"),
				description: t("settings.integrations.loadError"),
				variant: "destructive"
			});
		} finally {
			setLoading(false);
		}
	};

	const handleConnectionResult = (status: string) => {
		if (status === 'success') {
			toast({
				title: t("settings.integrations.connectionSuccess"),
				description: t("settings.integrations.connectionCompleted"),
			});
			// Refresh integrations
			fetchIntegrations();
		} else if (status === 'failure') {
			toast({
				title: t("settings.integrations.connectionError"),
				description: t("settings.integrations.connectionFailed"),
				variant: "destructive"
			});
		}
	};

	const handleConnect = async (provider: string) => {

		// Don't allow connecting to disabled integrations
		if (provider === "whatsapp") {
			return;
		}

		// Handle email provider - show modal for provider choice
		if (provider === 'email') {
			setEmailProviderDialog(true);
			return;
		}

		// Handle Microsoft Teams - separate OAuth flow
		if (provider === 'microsoft_teams') {
			await connectMicrosoftTeams();
			return;
		}

		await connectToProvider(provider);
	};

	const connectToProvider = async (actualProvider: string) => {
		try {

			setProcessingProvider(actualProvider === 'google' || actualProvider === 'outlook' ? 'email' : actualProvider);
			
			toast({
				title: t("settings.integrations.connectionInitiated"),
				description: t("settings.integrations.connecting", { provider: actualProvider }),
			});
			
			// Determine the command based on provider
			let command = '';
			
			switch (actualProvider) {
				case 'linkedin':
					command = 'generate-linkedin-hosted-wizard';
					break;
				case 'google':
					command = 'generate-google-hosted-wizard';
					break;
				case 'outlook':
					command = 'generate-microsoft-hosted-wizard';
					break;
				default:
					throw new Error(`Unsupported provider: ${actualProvider}`);
			}

            let isLocalHost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

			// Call the Supabase edge function to start the auth process
			const { data, error } = await supabase.functions.invoke(`unipile-manager?command=${command}${isLocalHost ? '&localhost=T' : ''}`, {
				method: 'GET'
			});
			
			if (error) {
				throw error;
			}
			
			// Redirect the user to the authentication URL
			if (data && data.url) {
				window.location.href = data.url;
			} else {
				throw new Error(t("settings.integrations.errors.noAuthUrl"));
			}
			
		} catch (error) {
			console.error(`Error connecting to ${actualProvider}:`, error);
			toast({
				title: t("settings.integrations.errors.connectionError"),
				description: t("settings.integrations.errors.connectionFailed"),
				variant: "destructive"
			});
			setProcessingProvider(null);
		}
	};

	const connectMicrosoftTeams = async () => {
		try {
			setProcessingProvider('microsoft_teams');

			toast({
				title: t("settings.integrations.connectionInitiated"),
				description: t("settings.integrations.connecting", { provider: "Microsoft Teams" }),
			});

			let isLocalHost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

			const { data, error } = await supabase.functions.invoke(
				`microsoft-transcript-auth?command=authorize${isLocalHost ? '&localhost=T' : ''}`,
				{ method: 'GET' }
			);

			if (error)
				throw error;

			if (data && data.url)
				window.location.href = data.url;
			else
				throw new Error(t("settings.integrations.errors.noAuthUrl"));

		} catch (error) {
			console.error("Error connecting to Microsoft Teams:", error);
			toast({
				title: t("settings.integrations.errors.connectionError"),
				description: t("settings.integrations.errors.connectionFailed"),
				variant: "destructive"
			});
			setProcessingProvider(null);
		}
	};

	const disconnectMicrosoftTeams = async () => {
		try {
			setProcessingProvider('microsoft_teams');

			const { error } = await supabase.functions.invoke(
				'microsoft-transcript-auth?command=disconnect',
				{ method: 'POST' }
			);

			if (error)
				throw error;

			await fetchIntegrations();

			toast({
				title: t("settings.integrations.success.disconnected"),
				description: t("settings.integrations.success.disconnectedDesc"),
			});
		} catch (error) {
			console.error("Error disconnecting Microsoft Teams:", error);
			toast({
				title: t("settings.integrations.errors.disconnectionError"),
				description: t("settings.integrations.errors.disconnectionFailed"),
				variant: "destructive"
			});
		} finally {
			setProcessingProvider(null);
			setDisconnectDialog({ open: false, provider: "", accountId: undefined });
		}
	};

	const handleDisconnect = async (provider: string, accountId?: string) => {
		if (!accountId) return;

		// Microsoft Teams uses a separate disconnect flow
		if (provider === 'microsoft_teams') {
			await disconnectMicrosoftTeams();
			return;
		}

		try {
			setProcessingProvider(provider);

			// Call the edge function to handle disconnection
			const { error } = await supabase.functions.invoke('unipile-manager?command=disconnect-account', {
				method: 'POST',
				body: { accountId }
			});

			if (error) {
				console.error("Error disconnecting account:", error);
				throw error;
			}

			// Refresh integrations
			await fetchIntegrations();
			
			toast({
				title: t("settings.integrations.success.disconnected"),
				description: t("settings.integrations.success.disconnectedDesc"),
			});
		} catch (error) {
			console.error(`Error disconnecting ${provider}:`, error);
			toast({
				title: t("settings.integrations.errors.disconnectionError"),
				description: t("settings.integrations.errors.disconnectionFailed"),
				variant: "destructive"
			});
		} finally {
			setProcessingProvider(null);
			setDisconnectDialog({ open: false, provider: "", accountId: undefined });
		}
	};

	const handleResync = async (provider: string, accountId?: string) => {
		if (!accountId) return;

		// Microsoft Teams uses transcript fetch for resync
		if (provider === 'microsoft_teams') {
			try {
				setResyncingProvider(provider);
				toast({
					title: t("settings.integrations.resync.initiated"),
					description: t("settings.integrations.resync.inProgress"),
				});

				const { data, error } = await supabase.functions.invoke(
					'microsoft-transcript-fetch?command=fetch-transcripts',
					{ method: 'POST' }
				);

				if (error)
					throw error;

				toast({
					title: t("settings.integrations.resync.success"),
					description: t("settings.integrations.microsoftTeams.syncSuccess", {
						fetched: data?.fetched || 0,
						linked: data?.linked || 0,
					}) || `Synced ${data?.fetched || 0} transcripts, linked ${data?.linked || 0} to candidates`,
				});
			} catch (error) {
				console.error("Error syncing Microsoft Teams transcripts:", error);
				toast({
					title: t("settings.integrations.resync.error"),
					description: t("settings.integrations.resync.errorDesc"),
					variant: "destructive"
				});
			} finally {
				setResyncingProvider(null);
			}
			return;
		}

		try {
			setResyncingProvider(provider);

			toast({
				title: t("settings.integrations.resync.initiated"),
				description: t("settings.integrations.resync.inProgress"),
			});

			const sixMonthsAgo = new Date();
			sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
			const dateFrom = sixMonthsAgo.toISOString().split('T')[0];

			const { data, error } = await supabase.functions.invoke(
				`unipile-handler?command=force-resync-account&account_id=${accountId}&date_from=${dateFrom}`,
				{ method: 'GET' }
			);

			if (error)
				throw error;

			if (data?.error)
				throw new Error(data.error);

			toast({
				title: t("settings.integrations.resync.success"),
				description: t("settings.integrations.resync.successDesc", { 
					count: data?.conversations_synced || 0 
				}),
			});

			await fetchIntegrations();
		} catch (error) {
			console.error(`Error resyncing ${provider}:`, error);
			toast({
				title: t("settings.integrations.resync.error"),
				description: t("settings.integrations.resync.errorDesc"),
				variant: "destructive"
			});
		} finally {
			setResyncingProvider(null);
		}
	};

	const handleOpenCalendarSettings = async () => {
		const accountId = integrations.email?.accountId;
		if (!accountId) return;

		setCalendarSettingsDialog(true);
		setLoadingCalendars(true);

		try {
			const { data, error } = await supabase.functions.invoke(
				`unipile-manager?command=list-calendars&account_id=${accountId}`,
				{ method: 'GET' }
			);

			if (error) throw error;

			setCalendars(data?.calendars || []);
			setSelectedCalendarId(data?.selected_calendar || null);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("settings.integrations.calendar.loadError"),
				variant: "destructive"
			});
			setCalendarSettingsDialog(false);
		} finally {
			setLoadingCalendars(false);
		}
	};

	const handleSelectCalendar = async (calendarId: string, calendarName: string) => {
		const accountId = integrations.email?.accountId;
		if (!accountId) return;

		setSavingCalendar(true);

		try {
			const { data, error } = await supabase.functions.invoke(
				`unipile-manager?command=set-selected-calendar&account_id=${accountId}`,
				{
					method: 'POST',
					body: {
						account_id: accountId,
						calendar_id: calendarId,
						calendar_name: calendarName
					}
				}
			);

			if (error) throw error;

			setSelectedCalendarId(calendarId);
			toast({
				title: t("settings.integrations.calendar.saved"),
				description: t("settings.integrations.calendar.savedDesc", { name: calendarName }),
			});
			setCalendarSettingsDialog(false);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("settings.integrations.calendar.saveError"),
				variant: "destructive"
			});
		} finally {
			setSavingCalendar(false);
		}
	};

	const getStatusIcon = (status?: string) => {
		switch (status) {
			case 'SYNC_SUCCESS':
			case 'CREATION_SUCCESS':
			case 'OK':
			case 'RECONNECTED':
				return <CheckCircle className="h-4 w-4 text-green-500" />;
			case 'CONNECTING':
				return <Clock className="h-4 w-4 text-orange-500" />;
			case 'CREDENTIALS':
			case 'ERROR':
			case 'STOPPED':
				return <XCircle className="h-4 w-4 text-red-500" />;
			default:
				return <AlertCircle className="h-4 w-4 text-gray-500" />;
		}
	};

	const getStatusText = (status?: string) => {
		switch (status) {
			case 'SYNC_SUCCESS':
			case 'CREATION_SUCCESS':
			case 'OK':
			case 'RECONNECTED':
				return t("settings.integrations.status.connected");
			case 'CONNECTING':
				return t("settings.integrations.status.connecting");
			case 'CREDENTIALS':
				return t("settings.integrations.status.credentialsError");
			case 'ERROR':
			case 'STOPPED':
				return t("settings.integrations.status.error");
			default:
				return t("settings.integrations.status.unknown");
		}
	};

	const getProviderDisplayName = (providerType?: string) => {
		switch (providerType?.toLowerCase()) {
			case 'google':
			case 'google_oauth':
				return 'Google';
			case 'outlook':
				return 'Outlook';
			case 'linkedin':
				return 'LinkedIn';
			case 'whatsapp':
				return 'WhatsApp';
			case 'microsoft_teams':
				return 'Microsoft Teams';
			default:
				return providerType;
		}
	};

	const renderIntegrationCard = (
		provider: string,
		icon: React.ReactNode,
		title: string,
		description: string,
		available: boolean = true
	) => {
		const integration = integrations[provider];
		const isProcessing = processingProvider === provider;
		const isResyncing = resyncingProvider === provider;
		
		return (
			<Card key={provider} className="p-4 flex flex-col justify-between min-h-[280px] min-w-[220px] max-w-[220px] transition-all duration-200 ease-in-out hover:scale-105 hover:shadow-lg hover:z-10 cursor-pointer group relative">
				{provider === 'email' && integration?.connected && (
					<button
						onClick={(e) => {
							e.stopPropagation();
							handleOpenCalendarSettings();
						}}
						className="absolute top-2 right-2 p-1 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
						title={t("settings.integrations.calendar.settings")}
					>
						<Settings className="h-4 w-4 text-gray-400 hover:text-gray-600" />
					</button>
				)}
				<div className="flex flex-col items-center text-center space-y-3">
					<div className="flex-shrink-0">
						{icon}
					</div>
					<div className="flex-1">
						<h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm leading-tight">
							{title}
						</h3>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
							{description}
						</p>
						
						{integration?.connected && (
							<div className="flex flex-col items-center space-y-1 mt-2">
								<div className="flex items-center justify-center space-x-1">
									{getStatusIcon(integration.status)}
									<span className="text-xs font-medium">
										{getStatusText(integration.status)}
									</span>
								</div>
								{provider === 'email' && integration.providerType && (
									<div className="flex items-center justify-center">
										<Badge variant="secondary" className="text-xs py-0.5 px-2">
											{getProviderDisplayName(integration.providerType)}
										</Badge>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
				
				<div className="flex flex-col space-y-2 mt-4 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
					{provider === 'linkedin' && (
						<Button
							variant="outline"
							size="sm"
							className="text-blue-500 border-blue-500 hover:bg-blue-50 text-xs h-7 px-3 group-hover:scale-105 transition-transform duration-200"
							onClick={() => window.open('https://chromewebstore.google.com/detail/digger-linkedin-recruiter/finfpbllggjghmklamaghmamoekfjeim', '_blank')}
						>
							{t("settings.integrations.chromeExtension")}
						</Button>
					)}
					
					{/* Hide connect/disconnect buttons if there are incomplete accounts */}
					{!hasIncompleteAccounts && (
						isProcessing ? (
							<div className="flex items-center justify-center space-x-2">
								<Loader2 className="h-4 w-4 animate-spin" />
								<span className="text-xs text-gray-500 dark:text-gray-400">
									{t("settings.integrations.processing")}
								</span>
							</div>
						) : available ? (
							integration?.connected ? (
								<>
									<Button
										variant="outline"
										size="sm"
										onClick={() => handleResync(provider, integration.accountId)}
										disabled={isResyncing}
										className="text-blue-500 border-blue-500 hover:bg-blue-50 text-xs h-7 px-3 group-hover:scale-105 transition-transform duration-200"
									>
										{isResyncing ? (
											<>
												<Loader2 className="h-3 w-3 animate-spin mr-1" />
												{t("settings.integrations.resyncing")}
											</>
										) : (
											<>
												<RefreshCw className="h-3 w-3 mr-1" />
												{t("settings.integrations.resync.button")}
											</>
										)}
									</Button>
									<Button
										variant="outline"
										size="sm"
										onClick={() => setDisconnectDialog({ 
											open: true, 
											provider, 
											accountId: integration.accountId 
										})}
										className="text-red-600 border-red-600 hover:bg-red-50 text-xs h-7 px-3 group-hover:scale-105 transition-transform duration-200"
									>
										{t("settings.integrations.disconnect")}
									</Button>
								</>
							) : (
								<Button
									variant="outline"
									size="sm"
									onClick={() => handleConnect(provider)}
									className="text-xs h-7 px-3 group-hover:scale-105 transition-transform duration-200"
								>
									{t("settings.integrations.connect")}
								</Button>
							)
						) : (
							<Badge variant="secondary" className="text-xs py-1 px-3 group-hover:scale-105 transition-transform duration-200">
								{t("settings.integrations.notAvailable")}
							</Badge>
						)
					)}
				</div>
			</Card>
		);
	};

	console.log("Component rendering - hasIncompleteAccounts:", hasIncompleteAccounts);

	return (
		<div className="space-y-8">
			<div>
				<h2 className="text-lg font-medium">{t("settings.integrations.title")}</h2>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					{t("settings.integrations.description")}
				</p>
			</div>

			{/* Connection Progress Banner */}
			{hasIncompleteAccounts && <ConnectionProgressBanner />}

			{/* All Integrations */}
			<div>
				<h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">
					{t("settings.integrations.sections.available")}
				</h3>
				<div className="flex flex-wrap gap-4">
					{renderIntegrationCard(
						"linkedin",
						<Linkedin className="h-7 w-7 text-blue-500" />,
						t("settings.integrations.linkedin.title"),
						t("settings.integrations.linkedin.description")
					)}
					{renderIntegrationCard(
						"email",
						<Mail className="h-7 w-7 text-green-600" />,
						t("settings.integrations.email.title"),
						t("settings.integrations.email.description")
					)}
					{renderIntegrationCard(
						"whatsapp",
						<MessageCircle className="h-7 w-7 text-gray-400" />,
						t("settings.integrations.whatsapp.title"),
						t("settings.integrations.whatsapp.description"),
						false
					)}
					{transcriptsEnabled && renderIntegrationCard(
						"microsoft_teams",
						<Video className="h-7 w-7 text-purple-600" />,
						t("settings.integrations.microsoftTeams.title", "Microsoft Teams"),
						t("settings.integrations.microsoftTeams.description", "Import meeting transcripts from Teams")
					)}
				</div>
			</div>

			{/* Disconnect Confirmation Dialog */}
			<AlertDialog 
				open={disconnectDialog.open} 
				onOpenChange={(open) => setDisconnectDialog({ ...disconnectDialog, open })}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("settings.integrations.confirmDisconnect.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("settings.integrations.confirmDisconnect.description")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("settings.integrations.confirmDisconnect.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => handleDisconnect(disconnectDialog.provider, disconnectDialog.accountId)}
							className="bg-red-600 hover:bg-red-700"
						>
							{t("settings.integrations.confirmDisconnect.confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Email Provider Selection Modal */}
			<Dialog open={emailProviderDialog} onOpenChange={setEmailProviderDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{t("settings.integrations.email.selectProvider") || "Select Email Provider"}
						</DialogTitle>
						<DialogDescription>
							{t("settings.integrations.email.selectProviderDescription") || "Choose which email provider you want to connect to:"}
						</DialogDescription>
					</DialogHeader>
					<div className="flex flex-col space-y-3 py-4">
						<Button
							onClick={() => {
								setEmailProviderDialog(false);
								connectToProvider('google');
							}}
							className="flex items-center justify-start space-x-3 h-12"
							variant="outline"
						>
							<Mail className="h-5 w-5 text-red-500" />
							<span>Google (Gmail)</span>
						</Button>
						<Button
							onClick={() => {
								setEmailProviderDialog(false);
								connectToProvider('outlook');
							}}
							className="flex items-center justify-start space-x-3 h-12"
							variant="outline"
						>
							<Mail className="h-5 w-5 text-blue-500" />
							<span>Microsoft (Outlook)</span>
						</Button>
					</div>
					<DialogFooter>
						<Button
							onClick={() => setEmailProviderDialog(false)}
							variant="ghost"
						>
							{t("settings.integrations.cancel") || "Cancel"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Calendar Settings Dialog */}
			<Dialog open={calendarSettingsDialog} onOpenChange={setCalendarSettingsDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{t("settings.integrations.calendar.title")}
						</DialogTitle>
						<DialogDescription>
							{t("settings.integrations.calendar.description")}
						</DialogDescription>
					</DialogHeader>
					<div className="flex flex-col space-y-2 py-4 max-h-[400px] overflow-y-auto">
						{loadingCalendars ? (
							<div className="flex items-center justify-center py-8">
								<Loader2 className="h-6 w-6 animate-spin text-gray-400" />
							</div>
						) : calendars.length === 0 ? (
							<p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
								{t("settings.integrations.calendar.noCalendars")}
							</p>
						) : (
							calendars.map((calendar) => (
								<button
									key={calendar.id}
									onClick={() => handleSelectCalendar(calendar.id, calendar.name)}
									disabled={savingCalendar}
									className={`flex items-center space-x-3 p-3 rounded-sm border transition-colors text-left w-full ${
										selectedCalendarId === calendar.id
											? 'border-blue-500 bg-blue-50 dark:bg-blue-950/50'
											: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
									} ${savingCalendar ? 'opacity-50 cursor-not-allowed' : ''}`}
								>
									<div
										className="w-3 h-3 rounded-full flex-shrink-0"
										style={{ backgroundColor: calendar.background_color || '#4986e7' }}
									/>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
											{calendar.name}
										</p>
										{calendar.is_primary && (
											<p className="text-xs text-gray-500 dark:text-gray-400">
												{t("settings.integrations.calendar.primary")}
											</p>
										)}
									</div>
									{selectedCalendarId === calendar.id && (
										<CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
									)}
								</button>
							))
						)}
					</div>
					{savingCalendar && (
						<div className="flex items-center justify-center space-x-2 py-2">
							<Loader2 className="h-4 w-4 animate-spin" />
							<span className="text-sm text-gray-500 dark:text-gray-400">
								{t("settings.integrations.calendar.saving")}
							</span>
						</div>
					)}
					<DialogFooter>
						<Button
							onClick={() => setCalendarSettingsDialog(false)}
							variant="ghost"
							disabled={savingCalendar}
						>
							{t("settings.integrations.cancel") || "Cancel"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};
